/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description STR List Item
 */
/** @jsx jsx */
import { jsx } from '@emotion/react'
import { selectLongTerm } from '@zcode-ui/filters'
import { ListingCardSizes } from '@zcode-ui/web-zumper-features'
import { type AnalyticsC, Origin } from '@zcode/nr-utils'
import { bookingProviders } from '@zcode/nr/es/shared/constants/source'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import {
  CLICK_LIST_CARD_1,
  CLICK_LIST_CARD_1_2_3,
  CLICK_LIST_CARD_2,
  CLICK_LIST_CARD_3,
} from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import type { Listable } from '@zcode/state-type'
import { type KeyboardEvent, type MouseEvent, type MouseEventHandler, memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useDispatch, useSelector } from 'react-redux'
import { mixpanelShortTermDetailClick } from '../../../../../modules/mixpanel/thunks/mixpanelShortTermDetailClick'
import { useTrackImpression } from '../ListItem/hooks/useTrackImpression'
import { ListingCard } from '../ListingCard/ListingCard'

export interface RailListingCardProps {
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
  entity: Listable
  forceIntersected?: boolean
  index: number
  isInitialLocationChanged: boolean
  isSimilarListings?: boolean
  onClick: (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void
  onClickQuickLook: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
  onClickThirdPartyListCard: () => void
  onMouseEnter: MouseEventHandler<HTMLDivElement>
  onMouseLeave: MouseEventHandler<HTMLDivElement>
  origin: Origin
  page: number
  shouldPreloadImage?: boolean
  trackListItemImpression: () => void
}

export const RailListingCard = memo(
  ({
    analyticChannel,
    entity,
    forceIntersected,
    index,
    isInitialLocationChanged,
    isSimilarListings,
    onClick,
    onClickQuickLook,
    onClickThirdPartyListCard,
    onMouseEnter,
    onMouseLeave,
    origin,
    page,
    shouldPreloadImage,
    trackListItemImpression,
  }: RailListingCardProps) => {
    const dispatch = useDispatch()
    const [ref, inView] = useInView({ threshold: 0.95 })
    useTrackImpression({
      entity,
      index,
      inView,
      onImpression: trackListItemImpression,
    })
    const { feedName } = entity
    const isLongTerm = useSelector(selectLongTerm)
    const eventKeys = [CLICK_LIST_CARD_1, CLICK_LIST_CARD_2, CLICK_LIST_CARD_3]

    const providers: readonly string[] = bookingProviders
    const shouldOpenThirdParty = !!feedName && providers.includes(feedName) && !isLongTerm

    const handleOnClick = (e: MouseEvent<HTMLElement>) => {
      if (index < eventKeys.length) {
        dispatch(Optimizely.track({ eventKey: eventKeys[index] }))
        dispatch(Optimizely.track({ eventKey: CLICK_LIST_CARD_1_2_3 }))
      }
      if (shouldOpenThirdParty && isInitialLocationChanged) {
        onClickThirdPartyListCard()
      } else {
        onClick(e)
      }
    }

    const handleDetailClick = (
      e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>
    ) => {
      e.stopPropagation()
      !isLongTerm && dispatch(mixpanelShortTermDetailClick({ entity, index, page }))
      onClick(e)
    }

    const similarListingProps = {
      cardContainerProps: {
        maxHeight: { base: '408px', md: '400px' },
        maxWidth: '360px',
        minWidth: { base: '312px', md: '360px' },
        boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
        borderRadius: 'lg',
        scrollSnapAlign: 'start',
      },
      cardContentContainerProps: { p: 'var(--chakra-space-4)' },
      hideCTAs: true,
      hideQuickLook: true,
      isShortTerm: false,
      imageProps: {
        borderRadius: '0',
      },
    } as const

    return (
      <ListingCard
        entity={entity}
        forceIntersected={forceIntersected}
        onClick={handleOnClick}
        onClickQuickLook={onClickQuickLook}
        onDetailClick={handleDetailClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={ref}
        shouldPreloadImage={shouldPreloadImage}
        size={ListingCardSizes.LG}
        {...(isSimilarListings
          ? similarListingProps
          : {
              analyticChannel,
              isShortTerm: !isLongTerm,
              origin,
              paidPhoneMixpanelOrigin: Origin.LISTABLES,
            })}
      />
    )
  }
)
